<template>
  <div class="quiz-modal flat-scroll">
    <div class="quiz-modal__modal quiz-modal__modal--lab-running">
      <div v-if="!isLoading" class="quiz-modal__wrapper">
        <DialogModal
          v-if="showAnotherAssessmentWarning"
          class="quiz-modal__dialog-modal quiz-modal__another-asmt-modal"
          @close-dialog-modal="closeModal()"
        >
          <p class="dialog-modal__title quiz-modal__dialog-modal-title">
            You already have a Sonar assessment in progress.
          </p>
          <p class="dialog-modal__info quiz-modal__dialog-modal-info">
            Please note that only one assessment can be taken at a time. If you
            choose to start a new assessment, your ongoing Sonar assessment will
            be terminated.
          </p>
          <p class="dialog-modal__info quiz-modal__dialog-modal-info">
            Do you want to continue ?
          </p>
          <div
            class="dialog-modal__actions-wrapper quiz-modal__dialog-modal-cta"
          >
            <button
              class="btn btn--primary"
              @click="getAssessmentAttempt((useLocalSorage = false))"
            >
              Yes, continue
            </button>
            <button class="btn btn--secondary" @click="closeModal">
              Cancel
            </button>
          </div>
        </DialogModal>
        <DialogModal
          v-if="isAssessmentOutOfTime"
          class="quiz-modal__dialog-modal"
          @close-dialog-modal="closeModal()"
        >
          <p class="dialog-modal__title quiz-modal__dialog-modal-title">
            Time’s up!
          </p>
          <p class="dialog-modal__info quiz-modal__dialog-modal-info">
            It looks like you ran out of time. Your assessment was not
            submitted. Don't worry, you can always give it another try.
          </p>
          <div
            class="dialog-modal__actions-wrapper quiz-modal__dialog-modal-cta"
          >
            <button class="btn btn--primary" @click="restartAssessment()">
              Restart
            </button>
            <button class="btn btn--secondary" @click="closeModal">
              Close
            </button>
          </div>
        </DialogModal>
        <div v-else class="quiz-modal__content-wrapper">
          <SonarTitleBar
            v-if="!showResults"
            v-bind="{
              timer,
              isAssessmentRunning,
              hasLab,
            }"
            @close-modal="changeConfirmCloseModal(true)"
          />
          <div class="quiz-modal__body">
            <div class="quiz-modal__quiz-container">
              <SonarQuiz
                v-if="isAnyCurrentAssessment && !showResults"
                v-bind="{
                  hasLab,
                  isLabRunning,
                  firstLabQuestionPosition,
                  showWelcomeCard,
                  isCtaLoading,
                  isAssessmentRunning,
                }"
                :assessment="assessmentAttempt"
                @submit-exam="submitExamHandler"
                @is-answering-question="answeringQuestionHandler"
                @start-assessment="startAssessment()"
                @resume-assessment="resumeAssessment()"
              />
            </div>
          </div>
        </div>
      </div>
      <SonarLoader
        v-else
        class="quiz-modal__loader-wrapper"
        :name="'ball-beat'"
        :color="'#ffffff'"
        :size="1"
        :label="loadingLabel"
      />
    </div>
    <transition name="notification">
      <SonarAlertNotification
        v-if="confirmCloseModal"
        class="sonar-alert-notification__wrapper"
      >
        <p class="sonar-alert-notification__title">
          End the assessment?
        </p>
        <p class="sonar-alert-notification__info">
          Your progress will not be saved.
        </p>
        <div class="sonar-alert-notification__cta">
          <button
            class="btn btn--primary"
            @click="changeConfirmCloseModal(false)"
          >
            Continue assessment
          </button>
          <button class="btn btn--secondary" @click="abortModal">
            End assessment
          </button>
        </div>
      </SonarAlertNotification>
    </transition>
    <SonarResults
      v-if="showResults"
      :currentScore="currentScore"
      :recommendedContentItems="recommendedContentItems"
      @close-results-modal="showResults = false"
      @close-modal="closeModal"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import CONSTANTS from '@/constants'
import {
  getSonarAssessment,
  setValue,
  clearSonarAssessment,
} from '@/sonar/helpers/sessionStorageHandler'
import DialogModal from '@/components/modals/DialogModal.vue'
import SonarAlertNotification from '../SonarAlertNotification.vue'
import { sonarCommons } from '@/sonar/helpers/mixins'
import { recommendedContent } from '@/sonar/helpers/mixins'
import SonarQuiz from './SonarQuiz.vue'
import SonarTitleBar from './SonarTitleBar.vue'
import SonarResults from './SonarResults.vue'
import SonarLoader from '../SonarLoader.vue'

export default {
  name: 'SonarQuizModal',
  components: {
    SonarTitleBar,
    SonarQuiz,
    SonarResults,
    SonarLoader,
    DialogModal,
    SonarAlertNotification,
  },
  mixins: [sonarCommons, recommendedContent],
  data: () => {
    return {
      isLoading: false,
      loadingLabel: '',
      isAnyCurrentAssessment: false,
      showAnotherAssessmentWarning: false,
      timer: 0,
      assessmentInterval: null,
      sessionStorageAttempt: null,
      confirmCloseModal: false,
      isCtaLoading: false,
      recommendedContentItems: [],
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.from = from
    })
  },
  watch: {
    currentQuestion() {
      if (this.showAssessment) this.startTimer()
      if (this.isFirstLabAssessmentQuestion) this.setIsAssessmentRunning(false)
      this.handleLastLabQuestionSession()
      if (this.isCurrentLabQuestion) this.handleSwitchingLab()
    },
    canAnswerQuestions() {
      if (this.canAnswerQuestions) {
        this.setCurrentAssessmentOutOfTime(false)
        this.setIsAssessmentRunning(true)
      }
    },
    isAssessmentRunning() {
      this.isAssessmentRunning ? this.startTimer() : this.stopTimer()
    },

    async isAssessmentOutOfTime() {
      if (
        this.isAssessmentOutOfTime &&
        !this.isLastQuestionAnswered &&
        !this.isSubmittingQuestion
      ) {
        const payload = {
          assessment_attempt_id: this.assessmentAttempt.id,
          assessment_id: this.assessmentAttempt.assessment.id,
          status: CONSTANTS.SONAR.ASSESSMENT.STATUS.TIMECAPED,
        }
        await this.$store.dispatch('sonar/setAssessmentStatus', payload)
      }
    },
  },
  computed: {
    ...mapState({
      currentLab: state => state.labs.currentLab,
      currentLabSession: state => state.labs.currentLabSession,
    }),
    ...mapGetters({
      canAnswerQuestions: 'sonar/canAnswerQuestions',
      currentScore: 'sonar/currentScore',
      currentAssessmentsAttempts: 'sonar/currentAssessmentsAttempts',
      currentQuestion: 'sonar/currentQuestion',
      assessmentAttempt: 'sonar/assessmentAttempt',
      lastAssessment: 'sonar/lastAssessment',
      isAssessmentStarted: 'sonar/isAssessmentStarted',
      isAssessmentOutOfTime: 'sonar/isAssessmentOutOfTime',
      currentLearningArea: 'sonar/currentLearningArea',
      isChangingLabState: 'sonar/isChangingLabState',
      showWelcomeCard: 'sonar/showWelcomeCard',
      showResults: 'sonar/showResults',
      showAssessment: 'sonar/showAssessment',
      isAssessmentRunning: 'sonar/isAssessmentRunning',
    }),
    hasLab() {
      return this.assessmentAttempt.hasLab
    },
    isLabRunning() {
      return (
        this.isAssessmentStarted &&
        this.currentLabSession?.status ===
          CONSTANTS.LABS.SESSION_STATUS.STARTED &&
        this.currentLabSession?.lab.id ===
          this.currentQuestion.labData?.content_id
      )
    },
    firstLabQuestionPosition() {
      return this.assessmentAttempt?.questionsLabData?.firstLabQuestion
        ?.position
    },
    lastLabQuestionPosition() {
      return this.assessmentAttempt?.questionsLabData?.lastLabQuestion?.position
    },
    isFirstLabAssessmentQuestion() {
      return this.firstLabQuestionPosition === this.currentQuestion.position
    },
    hasToEndLab() {
      return (
        this.currentLabSession?.status ===
          CONSTANTS.LABS.SESSION_STATUS.STARTED &&
        this.lastLabQuestionPosition < this.currentQuestion?.position
      )
    },
    isCurrentLabQuestion() {
      return this.currentQuestion.hasLab
    },
    isDifferentLab() {
      return (
        this.currentQuestion.position !== 1 &&
        !this.isFirstLabAssessmentQuestion &&
        this.currentLab?.id &&
        this.currentQuestion?.labData?.content_id !== this.currentLab?.id
      )
    },
    assessmentSkillAreas() {
      return this.lastAssessment?.score_per_skill_area?.map(ska => ska.id) || []
    },
    difficulty() {
      return this.lastAssessment?.score_per_skill_area?.length
        ? this.lastAssessment.score_per_skill_area[0].level
        : ''
    },
  },
  methods: {
    ...mapMutations({
      setParentContent: 'content/CURRENT_PARENT',
      setIsSonarQuizRunning: 'sonar/IS_SONAR_QUIZ_RUNNING_UPDATED',
      setLastAssessment: 'sonar/LAST_ASSESSMENT_UPDATED',
    }),
    ...mapActions('sonar', [
      'setDisplayCorrectAnswers',
      'resetAssessmentAttemptStatus',
      'setCanAnswerQuestions',
      'setIsAssessmentRunning',
      'setCurrentAssessmentOutOfTime',
    ]),
    changeConfirmCloseModal(newValue) {
      this.confirmCloseModal = newValue
    },
    async toggleLab() {
      if (!this.hasLab || (this.hasLab && !this.isLabRunning)) return
      if (this.isLabRunning)
        this.updateSessionStatus(CONSTANTS.LABS.SESSION_STATUS.TERMINATED)
    },
    getMetaData() {
      const page =
        this.assessmentAttempt?.status ===
        CONSTANTS.SONAR.ASSESSMENT.STATUS.STARTED
          ? 'assessment'
          : 'summary'
      const metadata = {
        page,
      }
      if (page === 'assessment') {
        metadata.question_id = this.currentQuestion?.id
        metadata.question_position = this.currentQuestion?.position
        metadata.total_questions = this.assessmentAttempt?.total_questions
        metadata.time_spend_ms = this.timer
      }
      return metadata
    },
    abortModal() {
      const payload = {
        assessment_attempt_id: this.assessmentAttempt.id,
        assessment_id: this.assessmentAttempt.assessment.id,
        status: CONSTANTS.SONAR.ASSESSMENT.STATUS.ABORTED,
        metadata: this.getMetaData(),
      }
      this.$store.dispatch('sonar/setAssessmentStatus', payload)
      this.closeModal()
    },
    closeModal() {
      this.$nextTick(() => {
        this.$store.dispatch('sonar/setLastQuestionAnswered', false)
        this.toggleLab()
        this.getNextRoute()
        return this.setCurrentAssessmentOutOfTime(false)
      })
    },
    answeringQuestionHandler(value) {
      if (value) this.stopTimer()
    },
    async submitExamHandler() {
      this.toggleLab()
      this.loadingLabel = 'Submitting assessment'
      this.isLoading = true
      const response = await this.submitExam()
      if (!response) return clearInterval(this.assessmentInterval)
      try {
        const promises = []
        promises.push(
          this.$store.dispatch(
            'sonar/getUserScoreByLearningArea',
            this.currentLearningArea.id
          )
        )
        const payload = {
          skill_area_ids: this.assessmentSkillAreas,
          difficulty: this.difficulty,
        }
        this.recommendedContentItems = await this.getAlgoliaItems(payload)
        promises.push(
          this.$store.dispatch(
            'sonar/getAssessments',
            this.currentLearningArea.id
          )
        )
        Promise.allSettled(promises)
        this.$store.dispatch('sonar/updateAssessmentAttemptStatus', {
          current_step: CONSTANTS.SONAR.ASSESSMENT.STEP.RESULTS,
        })
        setValue({ current_step: CONSTANTS.SONAR.ASSESSMENT.STEP.RESULTS })
      } finally {
        this.isLoading = false
        this.loadingLabel = ''
      }
    },
    resumeAssessment() {
      this.setIsAssessmentRunning(true)
      this.setCanAnswerQuestions(true)
    },
    async startAssessment() {
      try {
        this.isCtaLoading = true
        const payload = {
          assessment_attempt_id: this.assessmentAttempt.id,
          assessment_id: this.assessmentAttempt.assessment.id,
          status: CONSTANTS.SONAR.ASSESSMENT.STATUS.STARTED,
        }
        await this.$store.dispatch('sonar/setAssessmentStatus', payload)
        this.setCurrentAssessmentOutOfTime(false)
        this.$store.dispatch('sonar/updateAssessmentAttemptStatus', {
          current_step: CONSTANTS.SONAR.ASSESSMENT.STEP.ASSESSMENT,
        })
        setValue({ current_step: CONSTANTS.SONAR.ASSESSMENT.STEP.ASSESSMENT })
        if (!this.currentQuestion?.hasLab) {
          this.setIsAssessmentRunning(true)
          this.setCanAnswerQuestions(true)
        }
      } catch (e) {
        this.$store.dispatch('sonar/updateAssessmentAttemptStatus', {
          current_step: CONSTANTS.SONAR.ASSESSMENT.STEP.SUMMARY,
        })
        setValue({ current_step: CONSTANTS.SONAR.ASSESSMENT.STEP.SUMMARY })
      } finally {
        this.isCtaLoading = false
      }
    },
    async submitExam() {
      try {
        const payload = {
          assessment_attempt_id: this.assessmentAttempt.id,
          assessment_id: this.assessmentAttempt.assessment.id,
        }
        return await this.$store.dispatch('sonar/submitAssessment', payload)
      } catch (e) {
        this.isLoading = false
      }
    },
    startTimer() {
      this.stopTimer()
      this.handleAssessmentTimeInterval(this.timer)
    },
    stopTimer() {
      return clearInterval(this.assessmentInterval)
    },
    handleLastLabQuestionSession() {
      if (!this.hasLab) return
      if (this.hasToEndLab) {
        this.updateSessionStatus(CONSTANTS.LABS.SESSION_STATUS.TERMINATED)
      }
    },
    handleSwitchingLab() {
      if (!this.isDifferentLab) return
      this.setIsAssessmentRunning(false)
      this.$store.dispatch('sonar/updateAssessmentAttemptLabStatus', {
        isSwitchingLabs: true,
      })
    },
    handleAssessmentTimeInterval(currentTime) {
      this.timer = currentTime
      setValue({ current_timer: this.timer })
      this.assessmentInterval = setInterval(() => {
        if (this.timer === 0) {
          this.stopTimer()
          return this.setCurrentAssessmentOutOfTime(true)
        }
        this.timer -= 1
        setValue({ current_timer: this.timer })
      }, 1000)
    },
    getNextRoute() {
      this.$router.push({
        name: CONSTANTS.SONAR.ROUTES.HOME,
        params: { learningArea: this.currentLearningArea.slug },
      })
    },
    async getAssessmentAttempt(useSessionStorage = true) {
      this.isLoading = true
      const { assessmentId } = this.$route.params
      if (this.sessionStorageAttempt && useSessionStorage) {
        const { assessmentAttemptId } = this.sessionStorageAttempt
        try {
          const payload = {
            assessment_id: assessmentId,
            assessment_attempt_id: assessmentAttemptId,
          }
          await this.$store.dispatch('sonar/getAssessmentAttempt', payload)
          const {
            position,
            current_timer,
            current_step,
          } = this.sessionStorageAttempt
          if (current_step === CONSTANTS.SONAR.ASSESSMENT.STEP.RESULTS)
            this.getNextRoute()
          const currentAssessmentQuestion = this.assessmentAttempt.questions[
            position - 1
          ]
          this.$store.dispatch('sonar/setCurrentQuestion', {
            ...currentAssessmentQuestion,
            position,
          })
          this.$store.dispatch('sonar/updateAssessmentAttemptStatus', {
            current_step,
          })
          this.setCanAnswerQuestions(
            current_step === CONSTANTS.SONAR.ASSESSMENT.STEP.ASSESSMENT
          )
          this.timer =
            current_timer || this.assessmentAttempt.timecap_in_seconds
          this.setIsAssessmentRunning(this.showAssessment)
        } catch (e) {
          this.getNextRoute()
        }
      } else {
        try {
          await this.$store.dispatch(
            'sonar/createAssessmentAttempt',
            assessmentId
          )
          this.$store.dispatch('sonar/setCurrentQuestion', {
            ...this.assessmentAttempt?.questions[0],
            position: 1,
          })
          const { id: assessmentAttemptId } = this.assessmentAttempt
          setValue({
            assessmentId,
            assessmentAttemptId,
            position: 1,
            learning_area: this.currentLearningArea,
            current_step: CONSTANTS.SONAR.ASSESSMENT.STEP.SUMMARY,
          })
          this.timer =
            this.assessmentAttempt?.timecap_in_seconds ||
            CONSTANTS.SONAR.ASSESSMENT.DEFAULT_TIMECAP
          this.$store.dispatch('sonar/updateAssessmentAttemptStatus', {
            labStatus: {
              isSwitchingLabs: false,
              isRestartingLab: false,
              isStartingLab: false,
            },
            current_step: CONSTANTS.SONAR.ASSESSMENT.STEP.SUMMARY,
          })
        } catch (e) {
          this.getNextRoute()
        }
      }
      this.isAnyCurrentAssessment = !!this.assessmentAttempt
      this.showAnotherAssessmentWarning = false
      this.setParentContent({
        id: this.assessmentAttempt.id,
        type: CONSTANTS.PARENT_CONTENT_TYPES_KEYS.ASSESSMENT,
      })
      this.isLoading = false
    },
    async restartAssessment() {
      try {
        this.isLoading = true
        this.resetAssessmentAttemptStatus()
        this.setCurrentAssessmentOutOfTime(false)
        await this.getAssessmentAttempt(false)
        this.isLoading = false
      } catch (e) {
        this.getNextRoute()
      }
    },
    resetAssessment() {
      clearInterval(this.assessmentInterval)
      clearSonarAssessment()
      this.setIsSonarQuizRunning(false)
      this.setDisplayCorrectAnswers(false)
    },
  },

  async created() {
    const body = document.querySelector('body')
    body.classList.add('modal-open')
    this.isLoading = true
    this.setCanAnswerQuestions(false)
    this.resetAssessmentAttemptStatus()
    await this.$store.dispatch(
      'sonar/getAssessmentAttemptsByStatus',
      CONSTANTS.SONAR.ASSESSMENT.STATUS.STARTED
    )
    this.sessionStorageAttempt = getSonarAssessment()
    const isCurrentAttemptStorage = this.currentAssessmentsAttempts?.some(
      at => at.id === this.sessionStorageAttempt?.assessmentAttemptId
    )
    const isAnyCurrentAssessmentsAttempts = this.currentAssessmentsAttempts
      .length
    if (isAnyCurrentAssessmentsAttempts && !isCurrentAttemptStorage) {
      this.isLoading = false
      this.showAnotherAssessmentWarning = true
    } else {
      await this.getAssessmentAttempt()
      this.isLoading = false
    }
  },
  mounted() {
    this.setIsSonarQuizRunning(true)
  },
  destroyed() {
    this.resetAssessment()
    const { assessmentId } = this.$route.params
    const isTakingAssessmentFromResults =
      assessmentId && assessmentId !== this.assessmentAttempt.id
    if (isTakingAssessmentFromResults) return
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
}
</script>

<style lang="scss">
.quiz-modal {
  &__body {
    display: block;
    flex-direction: column;
    width: 100%;
    @include tablet {
      display: flex;
      width: 100%;
      height: calc(100% - var(--sonar-modal-navbar-height));
      overflow: hidden;
    }
  }
  &__overview {
    padding: 87px 100px;
  }
  &__quiz-container {
    height: 100%;
    min-height: calc(100vh - var(--sonar-modal-navbar-height));
  }
  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 80%;
    flex: 1;
  }
  &__modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--glass-purpler), var(--dark-gray);
    z-index: 205;
    overflow-y: auto;
    &--lab-running {
      background: var(--menu-gradient), var(--dark-gray);
    }
  }
  &__wrapper {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    z-index: 206;
    position: relative;
  }
  &__content-wrapper {
    display: flex;
    margin: 0 auto;
    flex-wrap: wrap;
    max-width: 2800px;
    height: 100%;
    align-content: flex-start;
  }
  &__description {
    color: white;
    font-size: 16px;
  }
  &__button-wrapper {
    margin-left: auto;
  }
  &__description {
    &-wrapper {
      display: flex;
      height: 100%;
      flex-direction: column;
      flex: 1;
      img {
        max-width: 100%;
      }
    }

    display: flex;
    flex-direction: column;
    height: 100%;
  }
  &--remarked {
    background: var(--tango);
    color: var(--shark);
  }
  &__dialog-modal {
    &-title {
      font-size: rem(40);
      line-height: rem(52);
      font-weight: 700;
      text-align: center;
    }
    &-info {
      font-size: rem(18);
      line-height: rem(24);
      font-weight: 500;
      color: var(--light-blue-gray);
      text-align: center;
    }
    &-cta {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
    }
  }
}

.notification-enter-active {
  animation: notification-in 0.3s ease-in forwards;
}
.notification-leave-active {
  animation: notification-in 0.3s ease-in reverse forwards;
}
@keyframes notification-in {
  from {
    right: -300px;
  }
  to {
    right: 20px;
  }
}
</style>
